import React from "react";
import EditableHTML from "./EditableHTML";
import ReflectedHeading from "./ReflectedHeading";
import HeroCountdown from "./HeroCountdown";

const FALLBACK_HERO =
  "https://images.unsplash.com/photo-1615657220350-ad1f77adc232?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHx3YXRlciUyMGRhbWFnZSUyMHJlc3RvcmF0aW9uJTIwd29ya2VyfGVufDB8fHx8MTc3ODY3NjY1Nnww&ixlib=rb-4.1.0&q=85";

const stripPara = (html) => (html || "").replace(/<\/?p[^>]*>/g, "").trim();

// Convert stored rich-text HTML to plain visible text.
// The hero title/eyebrow/CTA fields are rendered as plain strings (not HTML),
// so any inline tags the editor produces — <font>, <span>, <strong>, etc. —
// need to be stripped, not just <p>. Uses DOMParser for safe entity decoding.
const toPlain = (html) => {
  if (!html) return "";
  try {
    const tmp = document.createElement("div");
    tmp.innerHTML = String(html);
    return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
  } catch {
    return stripPara(html);
  }
};

const clampScale = (raw) => {
  const n = Number(toPlain(raw));
  if (!Number.isFinite(n) || n <= 0) return 1;
  // Sane bounds so an accidental "50" doesn't destroy the layout
  return Math.min(4, Math.max(0.5, n));
};

const isOn = (raw) => {
  const v = toPlain(raw).toLowerCase();
  return v !== "false" && v !== "0" && v !== "off" && v !== "no";
};

const WaterHero = ({ content = {}, onPrimary, onSecondary, onTertiary }) => {
  const titleText = toPlain(content.hero_title) || "Restoring What Water Took";
  const eyebrowText = toPlain(content.hero_eyebrow) || "Certified Restoration Specialists";
  const primaryCta = toPlain(content.hero_primary_cta) || "Request a Free Quote";
  const secondaryCta = toPlain(content.hero_secondary_cta) || "Explore Equipment";
  const tertiaryCta = toPlain(content.hero_tertiary_cta) || "Contact Us";
  const heroBg = content.hero_image_url || FALLBACK_HERO;

  const titleScale = clampScale(content.hero_title_scale);
  const eyebrowScale = clampScale(content.hero_eyebrow_scale);
  const countdownOn = content.hero_countdown_enabled === undefined
    ? true
    : isOn(content.hero_countdown_enabled);
  const countdownHours = Math.max(1, Number(toPlain(content.hero_countdown_hours)) || 48);
  const countdownLabel = content.hero_countdown_label;

  return (
    <section
      className="relative min-h-[100vh] flex items-end overflow-hidden"
      data-testid="hero-section"
    >
      {/* Worker image — soft, blended into the water surface */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.35] mix-blend-luminosity"
        style={{ backgroundImage: `url(${heroBg})` }}
        aria-hidden="true"
      />
      {/* Atmospheric overlay — heavier at the top, fading to clear water near the headline */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/40 to-slate-950/80" aria-hidden="true" />
      {/* Glow halo behind the headline so reflection reads brighter */}
      <div
        className="absolute -bottom-32 left-1/4 right-1/4 h-[60vh] rounded-full blur-[140px] opacity-50 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(30,144,255,0.35), transparent 70%)" }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-32 pt-40 w-full">
        <div className="max-w-5xl animate-fade-up">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] uppercase tracking-[0.2em] text-slate-300 mb-8 backdrop-blur"
            style={{ fontSize: `calc(0.75rem * ${eyebrowScale})` }}
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-atc-green animate-pulse" />
            {eyebrowText}
          </div>

          <ReflectedHeading
            as="h1"
            className="font-display font-black tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl brand-gradient-text"
            reflectionScale={0.6}
            testId="hero-title"
            style={{ fontSize: `calc(clamp(2.75rem, 6vw + 1rem, 6rem) * ${titleScale})` }}
          >
            {titleText}
          </ReflectedHeading>

          {countdownOn && (
            <div style={{ marginTop: `calc(clamp(2.75rem, 6vw + 1rem, 6rem) * ${titleScale} * 1.05)` }}>
              <HeroCountdown hours={countdownHours} label={countdownLabel} />
            </div>
          )}

          <EditableHTML
            html={content.hero_subtitle}
            className="mt-10 text-base sm:text-lg text-slate-300 max-w-2xl"
            testId="hero-subtitle"
          />

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={onPrimary}
              className="px-7 py-4 rounded-full brand-gradient text-white text-sm font-semibold tracking-wide hover:opacity-95 transition shadow-[0_10px_40px_-10px_rgba(30,144,255,0.6)]"
              data-testid="hero-primary-cta"
            >
              {primaryCta}
            </button>
            <button
              onClick={onSecondary}
              className="px-7 py-4 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur text-slate-100 text-sm font-medium hover:border-white/30 transition"
              data-testid="hero-secondary-cta"
            >
              {secondaryCta}
            </button>
            <button
              onClick={onTertiary}
              className="px-7 py-4 rounded-full border border-atc-green/40 bg-atc-green/5 backdrop-blur text-atc-green text-sm font-medium hover:bg-atc-green/15 hover:border-atc-green/70 transition"
              data-testid="hero-tertiary-cta"
            >
              {tertiaryCta}
            </button>
          </div>
        </div>
      </div>

      {/* Subtle "shoreline" at the very bottom of hero */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
    </section>
  );
};

export default WaterHero;
